import React from 'react';
import { Link } from 'react-router-dom';
import './calendarItemDetail.scss';
import usePermissionsCalendar from 'permissions/modules/calendar/usePermissionsCalendar';
import usePermissionsAgenda from 'permissions/modules/agenda/usePermissionsAgenda';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { calendarComponentI18n } from '../i18n';

const priorityClassMap = {
  high: 'calendar-task--priority-high',
  medium: 'calendar-task--priority-medium',
  low: 'calendar-task--priority-low',
};

const priorityBadgeVariantMap = {
  high: 'error',
  medium: 'warning',
  low: 'success',
};

const CalendarItemDetail = ({
  DesignSystemComponents,
  title,
  time,
  code,
  clientName,
  assigneeName,
  priority,
  caseId,
  taskId,
  origin,
  mode,
  description,
  startDateTime,
  endDateTime,
  onEdit,
  onDelete,
  appointmentId,
  isMine,
}) => {
  const { SvgIcon, Badge, IconButton } = DesignSystemComponents;
  const { t } = useScopedTranslation(calendarComponentI18n);

  const priorityKeyMap = { high: 'item_priority_high', medium: 'item_priority_medium', low: 'item_priority_low' };
  const priorityLabel = priorityKeyMap[priority] ? t(priorityKeyMap[priority]) : '';
  const priorityBadgeClass = priorityClassMap[priority] || '';
  const badgeVariant = priorityBadgeVariantMap[priority] || 'warning';
  const { canViewTaskDetail } = usePermissionsCalendar();
  const { canManageAppointment } = usePermissionsAgenda();

  const content = (
    <div
      className={`card mb-3 ${mode !== 'appointments' ? 'calendar-task-detail' : ''}`}
    >
      <div className='card-body'>
        <div className='d-flex justify-content-between align-items-start mb-2'>
          <div
            style={
              mode !== 'appointments'
                ? { maxWidth: '77vh', minWidth: 0 }
                : undefined
            }
            className={`${mode === 'appointments' ? 'w-100' : ''}`}
          >
            <div
              className={`${mode === 'appointments' ? 'd-flex justify-content-between align-items-center gap-2' : ''}`}
            >
              <p
                className={`mini fw-bold mb-2 d-block ${mode === 'tasks' ? 'ellipsis' : ''}`}
                title={
                  mode === 'appointments' && origin === 'outlook'
                    ? 'Outlook '
                    : title
                }
              >
                {mode === 'appointments' && origin === 'outlook'
                  ? 'Outlook '
                  : title}
              </p>
              {mode === 'appointments' &&
                origin === 'legal365' &&
                canManageAppointment && (
                  <div className='d-flex mb-2'>
                    <IconButton
                      ariaLabel={t('item_edit_event')}
                      icon='pencil'
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit?.(appointmentId);
                      }}
                      size='sm'
                      iconStroke='var(--primary-500)'
                    />
                    <IconButton
                      ariaLabel={t('item_delete_event')}
                      icon='trash'
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.(appointmentId);
                      }}
                      size='sm'
                      iconStroke='var(--error-500)'
                      className='ms-1'
                    />
                  </div>
                )}
            </div>

            {time && (
              <div className='color-gray-600 extra-small d-flex align-items-center gap-1'>
                <SvgIcon name='clock' size='14px' />
                <span>{time}</span>
              </div>
            )}
            {mode === 'appointments' && (
              <div className='d-flex align-items-center gap-1'>
                <div className='d-flex flex-column'>
                  <p className='caption color-gray-600'>{startDateTime}</p>
                  <p className='caption color-gray-600'>{endDateTime}</p>
                </div>
              </div>
            )}
          </div>
          {priority && (
            <div className='d-flex justify-content-between align-items-center'>
              {Badge ? (
                <Badge color={badgeVariant} size='sm' text={priorityLabel} />
              ) : (
                <span
                  className={`calendar-task-detail__priority badge rounded-pill ${priorityBadgeClass}`}
                >
                  {priorityLabel}
                </span>
              )}
            </div>
          )}
        </div>
        <div className='calendar-task-detail__meta extra-small'>
          {code && (
            <div className='mb-1 mini font-light'>
              <span>{t('item_role_label')}: </span>
              <span>{code}</span>
            </div>
          )}
          {clientName && (
            <div className='mb-1 mini font-light'>
              <span>{t('item_client_label')}: </span>
              <span>{clientName}</span>
            </div>
          )}
          {assigneeName && (
            <div className='mb-1 mini font-light'>
              <span>{t('item_assigned_label')}: </span>
              <span>{assigneeName}</span>
            </div>
          )}
          {mode === 'appointments' && (
            <div className='mini font-light'>
              <span>
                {origin === 'outlook' ? t('item_outlook_detail') : description}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (mode !== 'appointments' && caseId && taskId && canViewTaskDetail) {
    return (
      <Link
        to={`/detalle-causa/${caseId}/tareas`}
        state={{ taskIdToOpen: taskId }}
        className='text-decoration-none'
      >
        {content}
      </Link>
    );
  }

  return content;
};

export default CalendarItemDetail;
